import React from "react";
import styled from "styled-components";
import { colors, typography, spacing } from "../styles/designSystem";
import {
    Panel,
    PanelLabel,
    Arrow,
    Caption,
    Stage,
    StageName,
    StageDesc,
    DomainGrid,
    PipelineFlow,
    StepGrid,
} from "./diagrams/primitives";

/**
 * Diagrams for the NSA Brands case study.
 *
 * The rental flow is a state machine, the move-out check is a pipeline that can
 * refuse to answer, and Megamind is a data path that ends in a recommendation.
 * Each is drawn as structure rather than decoration — the fork at the end of the
 * vision pipeline is the point of that whole section.
 */

// ===== Rental state machine =====




const RENTAL_STATES = [
    { name: "Available", color: colors.gray[300] },
    { name: "Selected", color: colors.cyan[300] },
    { name: "Customer Created", color: colors.cyan[300] },
    { name: "Payment", color: colors.amber[400] },
    { name: "Agreement", color: colors.amber[400] },
    { name: "Rental Active", color: colors.primary[500] },
    { name: "Access Granted", color: colors.emerald[500] },
];

export const RentalStateFlow = () => (
    <StepGrid
        label="Rental — Modelled as State, Not Screens"
        steps={RENTAL_STATES}
        columns={4}
        caption="Every transition needs an explicit recovery path. Dropping a customer back to the start is not an option when the last state unlocks a physical door."
    />
);

// ===== Move-out vision pipeline, ending in a fork =====
const Fork = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${spacing[400]};
    margin-top: ${spacing[200]};

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const Branch = styled.div`
    border: 1px solid ${({ color }) => color};
    border-top-width: 3px;
    padding: ${spacing[500]};
    display: flex;
    flex-direction: column;
    gap: ${spacing[200]};
`;

const BranchCondition = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[150]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ color }) => color};
`;

const BranchOutcome = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[300]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.text};
`;

const PIPELINE = [
    { name: "Live Camera Capture", desc: "In-app only — no library uploads", color: colors.cyan[300] },
    { name: "Image Quality Check", desc: "Reject before the model ever sees it", color: colors.cyan[300] },
    { name: "Vision Analysis", desc: "What is actually in the unit", color: colors.primary[500] },
    { name: "Structured Response", desc: "Constrained output the app can validate", color: colors.primary[500] },
    { name: "Business Rules", desc: "Explicit move-out requirements", color: colors.amber[400] },
    { name: "Confidence Checks", desc: "Is this certain enough to act on?", color: colors.amber[400] },
];

export const VisionPipeline = () => (
    <Panel>
        <PanelLabel>Move-Out Validation &mdash; The Model Is One Signal</PanelLabel>
        {PIPELINE.map((stage, i) => (
            <React.Fragment key={stage.name}>
                <Stage color={stage.color}>
                    <StageName color={stage.color}>{stage.name}</StageName>
                    <StageDesc>{stage.desc}</StageDesc>
                </Stage>
                {i < PIPELINE.length - 1 && <Arrow aria-hidden="true">&#8595;</Arrow>}
            </React.Fragment>
        ))}
        <Arrow aria-hidden="true">&#8595;</Arrow>
        <Fork>
            <Branch color={colors.emerald[500]}>
                <BranchCondition color={colors.emerald[500]}>High Confidence</BranchCondition>
                <BranchOutcome>Approve &amp; Continue</BranchOutcome>
            </Branch>
            <Branch color={colors.coral[400]}>
                <BranchCondition color={colors.coral[400]}>Low Confidence</BranchCondition>
                <BranchOutcome>Escalate to Human</BranchOutcome>
            </Branch>
        </Fork>
        <Caption>
            The right-hand branch is the important one. A system that cannot tell should say so
            rather than guess — that boundary is the architecture, not a fallback.
        </Caption>
    </Panel>
);

// ===== Megamind: data to decisions =====
const MEGAMIND = [
    { name: "Internal + Market Data", desc: "Occupancy, revenue, availability, pricing, competitor pricing", color: colors.cyan[300] },
    { name: "Normalization", desc: "Different sources into one comparable shape", color: colors.cyan[300] },
    { name: "Business Logic", desc: "What counts as underperforming, here, this month", color: colors.primary[500] },
    { name: "Analysis", desc: "Where occupancy is moving and how pricing compares", color: colors.primary[500] },
    { name: "Recommendations", desc: "Not what happened — what to do next", color: colors.amber[400] },
    { name: "Operator", desc: "The person who acts on it", color: colors.emerald[500] },
];

export const MegamindFlow = () => (
    <PipelineFlow
        label="Megamind — Data to Decisions"
        stages={MEGAMIND}
        caption="Structured around operational questions rather than another set of dashboards, so the output is an action instead of a number."
    />
);

// ===== What the work spanned =====




const DOMAINS = [
    { name: "Mobile", desc: "Production customer applications and mobile-specific account workflows.", color: colors.primary[500] },
    { name: "Frontend", desc: "Customer-facing rental, account, kiosk, and operational experiences.", color: colors.cyan[300] },
    { name: "Product Architecture", desc: "State-driven workflows spanning customers, facilities, rentals, and internal operations.", color: colors.amber[400] },
    { name: "AI & Computer Vision", desc: "Structured outputs, image validation, confidence rules, and human-in-the-loop escalation.", color: colors.emerald[500] },
    { name: "Data Products", desc: "Operational analytics combining occupancy, revenue, pricing, and market information.", color: colors.purple[400] },
    { name: "Physical + Digital", desc: "Systems where application state directly affected real-world customer and facility operations.", color: colors.coral[400] },
];

export const BuildDomains = () => (
    <DomainGrid label="What the Work Spanned" domains={DOMAINS} />
);
